import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ArrowRight, MessageSquare, Mail } from "lucide-react";
import ShiftsLayout from "../../components/shifts/ShiftsLayout";
import PageMeta from "../../components/seo/PageMeta";
import { FAQ_GROUPS, SHIFTS_CTAS } from "../../data/marketplaceData";

export default function ShiftsFAQ() {
  const [open, setOpen] = useState({}); // key = `${groupIdx}-${itemIdx}`

  const toggle = (k) => setOpen((o) => ({ ...o, [k]: !o[k] }));

  /* JSON-LD for FAQPage */
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_GROUPS.flatMap((g) => g.items).map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };

  return (
    <ShiftsLayout>
      <PageMeta
        title="FAQ — TRITAL ShiftBridge™ by TRITAL Care®"
        description="Answers to the most-asked TRITAL ShiftBridge™ questions for clinicians, facilities, and procurement teams."
        canonical="https://www.tritalcare.com/shiftbridge/faq"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-trital-mesh">
        <div className="absolute inset-0 dot-grid opacity-50 pointer-events-none" />
        <div className="relative max-w-[900px] mx-auto px-5 lg:px-8 pt-14 pb-12 lg:pt-20 lg:pb-16 text-center">
          <div className="overline text-trital-red">FAQ</div>
          <h1 className="mt-3 font-display font-black text-5xl lg:text-[60px] tracking-tight leading-[1.02] text-slate-900 text-balance">
            Plain answers to the questions
            <span className="block text-trital-navy">people actually ask.</span>
          </h1>
          <p className="mt-5 text-slate-600 max-w-[58ch] mx-auto leading-relaxed">
            Three groups: clinicians, facilities, and platform / support. Don't see your question? Email <a className="text-trital-navy font-semibold hover:underline" href="mailto:support@tritalcare.com">support@tritalcare.com</a> — replied within 1 business hour.
          </p>
        </div>
      </section>

      {/* GROUPS */}
      <section className="max-w-[900px] mx-auto px-5 lg:px-8 py-14 lg:py-20 space-y-12">
        {FAQ_GROUPS.map((g, gi) => (
          <div key={g.audience} data-testid={`faq-group-${g.audience}`}>
            <div className="overline text-trital-red">{g.label}</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl text-slate-900 leading-tight text-balance">
              {g.audience === "clinician" && "If you take care of patients."}
              {g.audience === "facility" && "If you run a unit, hospital or SNF."}
              {g.audience === "general" && "Platform & support."}
            </h2>
            <ul className="mt-6 space-y-3">
              {g.items.map((it, ii) => {
                const k = `${gi}-${ii}`;
                const isOpen = !!open[k];
                return (
                  <li key={it.q} className="rounded-2xl border border-slate-200 bg-white shadow-card overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggle(k)}
                      aria-expanded={isOpen}
                      className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                      data-testid={`faq-q-${gi}-${ii}`}
                    >
                      <span className="font-display font-bold text-[15.5px] text-slate-900">{it.q}</span>
                      <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${isOpen ? "rotate-180 text-trital-navy" : ""}`} strokeWidth={1.8} />
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="overflow-hidden"
                        >
                          <p className="px-5 pb-5 text-[14px] text-slate-600 leading-relaxed">{it.a}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </section>

      {/* CTA BAND */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-90" />
        <div className="absolute inset-0 dot-grid opacity-10" />
        <div className="relative max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-20 grid sm:grid-cols-3 gap-4">
          <Link to={SHIFTS_CTAS.clinicianSignup} className="rounded-3xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors" data-testid="faq-cta-clinician">
            <div className="overline text-trital-red">Clinicians</div>
            <div className="mt-3 font-display font-bold text-xl">Create clinician account</div>
            <div className="mt-2 text-[12.5px] text-white/70">Free, 60 seconds.</div>
            <ArrowRight className="mt-4 w-4 h-4 text-trital-red" strokeWidth={1.8} />
          </Link>
          <Link to={SHIFTS_CTAS.facilitySignup} className="rounded-3xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors" data-testid="faq-cta-facility">
            <div className="overline text-trital-red">Facilities</div>
            <div className="mt-3 font-display font-bold text-xl">Post your first shift</div>
            <div className="mt-2 text-[12.5px] text-white/70">Approved in 1 business day.</div>
            <ArrowRight className="mt-4 w-4 h-4 text-trital-red" strokeWidth={1.8} />
          </Link>
          <Link to="/shifts/contact" className="rounded-3xl bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors" data-testid="faq-cta-support">
            <div className="overline text-trital-red">Support</div>
            <div className="mt-3 font-display font-bold text-xl inline-flex items-center gap-2"><MessageSquare className="w-4 h-4" strokeWidth={1.8} /> Talk to a human</div>
            <div className="mt-2 text-[12.5px] text-white/70">Real recruiter desk · 1-hour SLA.</div>
            <ArrowRight className="mt-4 w-4 h-4 text-trital-red" strokeWidth={1.8} />
          </Link>
        </div>
      </section>
    </ShiftsLayout>
  );
}
